import {
  createAsyncThunk,
  createSlice,
  isAnyOf,
  PayloadAction,
} from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { infoSeqIdCounter } from '../app/middleware';
import { DUMMY_RESTAURANT_CODE, OUT_OF_SESSION } from '../constants';
import {
  AgentTypes,
  CheckResponseTypes,
  CouponResponseTypes,
  EventTypes,
} from '../constants/event';
import { AIStatusColors } from '../redux/features/ai/ai.constants';
import { IAIState, ReusedAnswers } from '../redux/features/ai/ai.props';
import { ITaskRouterSlice } from '../redux/features/taskRouter/taskRouter.props';
import { selectIsWebSocketConnected } from '../selectors/message';
import { readEnvVariable } from '../utils';
import { agent } from '../utils/agent';
import { cyborgAlert } from '../utils/alert';
import { get } from '../utils/api';
import logger from '../utils/logger';
import { ILoyaltyTransmissionMessage } from './cartSlice.props';
import { selectMenuVersion } from './menuSlice';
import { filterBufferedEvents } from './messagingSlice.utils';
import {
  IOrderTransmissionMessage,
  ISendOrderLoyalty,
} from './orderSlice.props';
import { selectStage } from './restaurantSlice';
import { updateNonCriticalCheckScore } from '../redux/features/healthScore/healthScore.slice';
import { NonCriticalChecksNames } from '../redux/features/healthScore/healthScore.constants';
import { IBrandMenuOrderItem, IMUErrorResponse } from '../types/brandMenu';
import { Languages } from '../utils/constants';
import { OrderProcessingMetricData } from './messagingSlice.props';

export enum SessionSource {
  hitl = 'HITL',
  communicator = 'COMMUNICATOR',
}

export enum RestaurantStaffInterventionStatus {
  initial = 'INITIAL',
  open = 'OPEN',
  close = 'CLOSE',
}

export interface TransmissionMessage {
  seq: number;
  id: string;
  event: EventTypes;
  timestamp: string;
  agent_id: string;
  agent_type: AgentTypes;
  session_id: string;
  metadata?: any;
  restaurant_code?: string;
}

export interface IMessageReceived {
  messages: TransmissionMessage[];
  taskRouter?: ITaskRouterSlice;
  isSessionBufferedMessages?: boolean;
  sessionBoundarySessionId?: string;
  ai?: IAIState;
  FEATURE_FLAG_FINAL_ORDER_VALIDATION?: boolean;
  FEATURE_FLAG_LOYALTY_SUPPORT?: boolean;
}

export interface NewTransmissionMessage extends TransmissionMessage {
  data: {
    terminal_id: string;
    restaurant_code: string;
    session_id: string;
    media_format: string;
  };
}

export interface EndSessionTransmissionMessage extends TransmissionMessage {
  data: {
    restaurant_code: string;
    session_id: string;
  };
}

export interface AudioFrameTransmissionMessage extends TransmissionMessage {
  data: {
    payload: string;
    source_name: string;
  };
}

export enum BlockedReason {
  debounce = 'debounce',
  pendingAsr = 'pending asr',
}

export interface TextFrameTransmissionMessage extends TransmissionMessage {
  data: {
    status: 'hypothesis' | 'final' | 'TTS';
    payload: string;
    metadata?: any;
    hitl_mute?: boolean;
    blocked_status?: {
      blocked: boolean;
      reason?: BlockedReason;
      // For ORDERBOARD text events this may contain the original HITL
      // text event (including its metadata) that produced the TTS.
      // We intentionally keep this loose as it is a wire payload.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      source_event?: any;
    };
  };
}

export interface EntityMenuItem {
  id: number; // the id of the menu item or modifier and HITL will handle the time based menu and availabilty
  name: string;
  price: number;
  quantity: number;
  pos_specific_properties: {
    pos_id: number;
    pos_name?: string;
    modcode?: string;
  };
  children: EntityMenuItem[] | [];
  modifier_group_id: string;
  path_to_entity: string[] | [];
  is_auto_combined?: boolean;
  readback?: boolean;
  readbackName?: string;
}

export interface Hypothesis {
  dialog: string[];
  responses: string[];
  customer_intent: 'order' | 'other';
  confidence: number;
  order_items: EntityMenuItem[];
  is_brand_menu: boolean;
  brand_menu_order_items: IBrandMenuOrderItem[];
  loyalties?: ISendOrderLoyalty[];
  customer_info: {
    name: string;
  };
  // Populated by the AI (auto mode) only when the hypothesized order failed to
  // translate — same shape as the menu-module HTTP error response so HITL can
  // normalize and highlight it identically. Absent when the order is valid.
  menu_module_error?: IMUErrorResponse;
}

/**
 * Diagnostic card describing an item the AI heard the customer ask for that
 * does not exist in the menu and could not be resolved (after the say-again
 * retry + grace window). Populated by the producer only when the FF
 * `continue_ai_on_unresolved_items` is on. Unknown keys are forward-compatible.
 */
export interface NotOnMenuCard {
  item: string; // raw item text as the customer said it (lowercased / trimmed by producer)
  [key: string]: unknown; // forward-compat: ignore unknown producer-added keys
}

/**
 * Diagnostic card describing a constraint option (side, sauce, size, ...) the
 * customer requested that isn't valid for the parent item's constraint slot.
 */
export interface InvalidOptionLine {
  option: string; // option text the customer asked for, as inferred
  constraint: string | null; // constraint slot name (e.g. dipping_sauce); null when top-level
  is_top_level: boolean; // true when a tentative top-level item; false when a constraint child
  [key: string]: unknown; // forward-compat: ignore unknown producer-added keys
}

/**
 * Item whose requested quantity exceeds the per-item or brand-default threshold
 * (e.g. customer asked for 40 burgers; brand caps AI handling at 25). The AI
 * flagged the item but kept ordering under the FF; HITL should adjust manually.
 */
export interface QuantityEscalationItem {
  item_name: string; // top-level menu item name as resolved by the AI
  quantity: number; // quantity the AI inferred from the customer's utterance
  threshold: number; // threshold that was exceeded
  [key: string]: unknown;
}

/**
 * A required constraint with no default option that the customer's input
 * couldn't fill (e.g. meal requires a side but customer didn't name one).
 */
export interface NoDefaultOptionConstraint {
  constraint: string; // constraint slot name that has no default
  parent_item: string; // menu item that owns this constraint
  [key: string]: unknown;
}

/**
 * Coupon the customer mentioned that doesn't map to any menu offer.
 */
export interface UnmatchedCoupon {
  coupon: string; // coupon item as inferred by the AI
  has_related_offer: boolean; // always false today
  reason: string; // why the coupon wasn't applied (e.g. "no_related_offer_in_menu")
  [key: string]: unknown;
}

export type EntityType =
  | 'quantity'
  | 'menu_item'
  | 'discount'
  | 'coupon'
  | 'loyalty'
  | 'size';

export interface EntitySpan {
  start: number;
  end: number;
  type: EntityType;
  value: string;
}

export interface HypothesisTransmissionMessage extends TransmissionMessage {
  data: {
    hypotheses: Hypothesis[]; // will only have one hypothesis object in the hypotheses field for each session for now
    // Cumulative lists of items the AI could not resolve this session. Optional:
    // absent when the producer FF is off or nothing is unresolved. Treat absence
    // as an empty list. The producer ships the full cumulative list every event,
    // so consumers must replace (not append) and let it shrink on cart reset.
    not_on_menu_cards?: NotOnMenuCard[];
    invalid_option_lines?: InvalidOptionLine[];
    quantity_invalid?: QuantityEscalationItem[];
    no_default_option_constraints?: NoDefaultOptionConstraint[];
    unmatched_coupons?: UnmatchedCoupon[];
  };
}

export interface InfoTransmissionMessage extends TransmissionMessage {
  data:
    | {
        type: 'INFO' | 'METRIC' | 'LANGUAGE_SWITCH' | EventTypes.hypothesis;
        message: string;
        code?: string;
        metadata?: any; // This is really a json type per the spec, but... shocked TS doesn't have this.
      }
    | OrderProcessingMetricData;
}

export enum AgentInterceptionType {
  ORDER_CHANGED = 'ORDER_CHANGED',
  TYPING = 'TYPING',
  TTS_SENT = 'TTS_SENT',
  UNSUPERVISED_AI_ESCALATION = 'UNSUPERVISED_AI_ESCALATION',
  CANCEL_ORDER = 'CANCEL_ORDER',
  FINISH_ORDER = 'FINISH_ORDER',
}

export enum StaffInterceptionType {
  AGENT_REQUESTED_STAFF = 'AGENT_REQUESTED_STAFF',
}
export interface AgentInterceptionMessage extends TransmissionMessage {
  data: {
    type: AgentInterceptionType;
    description?: string;
  };
}

/**
 * HITL 3.0: an agent performance metric, discriminated by `data.metric`. The
 * first (and only, today) metric is `agent_response_time`, emitted when the
 * agent's per-turn response time exceeds the configured limit during a takeover
 * (see useAgentResponseTimeMetric).
 */
export interface IAgentMetricTransmissionMessage extends TransmissionMessage {
  data: {
    metric: 'agent_response_time';
    status: 'EXCEEDED';
    // Configured limit that was exceeded (AGENT_RESPONSE_TIME_LIMIT_SECONDS * 1000).
    threshold_ms: number;
    // ISO timestamp of the customer utterance whose reply is overdue.
    awaiting_since: string;
    // The customer utterance the agent has not yet replied to.
    customer_utterance: string;
  };
}

/**
 * HITL-18: the human agent's live working cart, emitted to the AI as read-only
 * input during takeover (the co-pilot input side). A full snapshot — not a
 * delta — and never a submit (must never be routed to POS).
 *
 * Item shape is `IBrandMenuOrderItem` (item name + nested `constraints[]`),
 * NOT the POS/order `ISendItemProperties` (id + pos_id + `children[]`). This is
 * deliberate: the AI's brand-menu cart is keyed by item NAME with constraints
 * (its own hypothesis even sends `id: 0` / empty `pos_specific_properties`), so
 * this is the shape the AI's hydrator (`generate_hydrated_cart`) actually
 * consumes. Built with the same `buildUnifiedMenuModuleOrder` converter the
 * brand-menu order submission uses, so it is 1:1 with a submitted order.
 */
export interface IAgentCartTransmissionMessage extends TransmissionMessage {
  data: {
    // Source-of-truth marker (HITL-17). `human` while the agent drives.
    cart_authority: 'human' | 'ai';
    final: boolean;
    // Correlation id: the AI echoes this in the ai_suggestion it sends back.
    request_id: string;
    items: IBrandMenuOrderItem[];
  };
}

/**
 * HITL 3.0: the human agent's explicit response to an `ai_suggestion` (the
 * "advise" side of the co-pilot). Emitted when the agent accepts or dismisses
 * the suggestion card. Correlated back to the suggestion via `suggestion_id`
 * and the originating cart via `request_id`. On accept-with-speech, `spoken_text`
 * carries the line spoken and `verbatim` marks whether it was spoken unchanged
 * (always true today — there is no edit-before-speak flow yet).
 */
export interface IAgentSuggestionResponseTransmissionMessage
  extends TransmissionMessage {
  data: {
    action: 'accepted' | 'dismissed';
    suggestion_id: string;
    request_id?: string;
    spoken_text?: string;
    verbatim?: boolean;
  };
}

export interface StaffInterceptionMessage extends TransmissionMessage {
  data: {
    type: StaffInterceptionType;
    description?: string;
  };
}

export interface StaffInfoTransmissionMessage extends TransmissionMessage {
  data: {
    payload: string;
    locale: string;
  };
}

interface ICouponApplyStatus {
  id: string;
  code: string;
  applied: boolean;
}

export interface CheckTransmissionMessage extends TransmissionMessage {
  data: {
    check?: {
      total: string;
      subtotal: string;
      tax: string;
      items?: any[];
    };
    error?: string;
    context?: string;
    display?: string;
    coupon_apply_status?: ICouponApplyStatus[];
    notification_enabled: boolean;
    request: string;
    status: CheckResponseTypes;
    transaction_id: string;
    request_id: string;
    final: boolean;
  };
}

export interface ICheckCouponTransmissionMessage extends TransmissionMessage {
  data: {
    couponno?: string;
    coupontext?: string;
    error?: string;
    status: CouponResponseTypes;
    transaction_id: string;
    request_id?: string;
  };
}

export interface ErrorTransmissionMessage extends TransmissionMessage {
  data: {
    message: string;
  };
}

export interface StaffInterVentionTransmissionMessage
  extends TransmissionMessage {
  restaurant_code: string;
  data: {
    type: string;
  };
}

export interface IStatusTransmissionMessage extends TransmissionMessage {
  data: {
    status: {
      code: number;
      type: string;
      message: string;
      additional_context?: string;
    };
    pos_partner: string;
    datetime: string;
    request_id?: string;
    result?: {
      transaction_id: string;
    };
  };
}

export interface IHITLSessionStartTransmissionMessage
  extends TransmissionMessage {
  data: {
    reason: string;
    brand_menu: boolean;
  };
}

export interface IHITLSessionEndTransmissionMessage
  extends TransmissionMessage {
  data: {
    reason: string;
  };
}

export interface IAIStatusTransmissionMessage extends TransmissionMessage {
  data: {
    status: AIStatusColors;
    message: string;
  };
}

/**
 * HITL 3.0: consolidated per-turn AI session state. The wire payload carries a
 * lot more (escalation, pending, order, …); we only type the fields the Pulse
 * card consumes today.
 */
export interface IAISessionStateTransmissionMessage
  extends TransmissionMessage {
  data: {
    // Conversation turn count for the live session.
    turn?: number;
    sentiment?: {
      label?: string;
    };
    reused_answers?: ReusedAnswers;
    express_mode?: {
      active?: boolean;
    };
  };
}

export interface IEntityAnnotationTransmissionMessage
  extends TransmissionMessage {
  data: {
    source_text_event_id: string;
    source_text: string;
    entities: EntitySpan[];
  };
}

export interface IHITLEventConnectionTransmissionMessage
  extends TransmissionMessage {
  data: {
    message: string;
  };
}

export interface IHITLAgentFirstActivityTransmissionMessage
  extends TransmissionMessage {
  data: {
    message: string;
  };
}

export interface ICancelOrderTransmissionMessage extends TransmissionMessage {
  data: {
    check_id: string;
    source: string;
    store_id: string;
    order_id: string;
    request_id: string;
  };
}

export interface IAIEscalationTransmissionMessage extends TransmissionMessage {
  data: {
    hypotheses: Hypothesis[];
    number_of_turns_before_handoff: number;
    customer_requested_handoff: boolean;
    escalation_reason: string;
    // true => AI halted and a human must take over now; false/absent => a
    // recoverable escalation the AI keeps ordering through.
    ai_requested_hitl_takeover?: boolean;
  };
}

export interface ILanguageSwitchTransmissionMessage
  extends TransmissionMessage {
  data: {
    new_language: Languages;
  };
}

export interface MessagingState {
  isEstablishingConnection: boolean;
  isConnected: boolean;
  isAudioWSConnected: boolean;
  isEstablishingAudioWSConnection: boolean;
  eventWSConnectionMessage: string;
  audioWSConnectionMessage: string;
  eventWSConnectionSuccess: boolean;
  audioWSConnectionSuccess: boolean;
  eventWSConnectionDurationMs: number | null;
  audioWSConnectionDurationMs: number | null;
  sentTextFrames: TextFrameTransmissionMessage[];
  inProgressHypothesisTextFrames: TextFrameTransmissionMessage[];
  sentInfo: Pick<InfoTransmissionMessage, 'data'>[];
  sentEnd: EndSessionTransmissionMessage[];
  sendOrder: IOrderTransmissionMessage[];
  sendAgentCart: IAgentCartTransmissionMessage[];
  sendLoyalty: ILoyaltyTransmissionMessage[];
  sendAgentFirstActivity: any[];
  sendAgentFirstInterception: any[];
  sendAgentInterception: any[];
  sendAgentMetric: IAgentMetricTransmissionMessage[];
  sendAgentSuggestionResponse: IAgentSuggestionResponseTransmissionMessage[];
  sendStaffInterception: any[];
  finalTextFrames: TextFrameTransmissionMessage[];
  startFrame: NewTransmissionMessage | null;
  hypothesisFrame: HypothesisTransmissionMessage | null;
  handledHypothesisFrames: Record<string, number[]>;
  currentSessionId: string;
  isRestaurantStaffJoined: Record<string, boolean>;
  isAIActive: boolean;
  isPlaying: boolean;
  isNewSession: boolean;
  isCarPresent: boolean;
  isStaffIntervention: boolean;
  isStaffIntervenedBeforeCarExit: boolean;
  sessionSource: SessionSource;
  isTTSOn: boolean;
  sentTTSRequest: TransmissionMessage[];
  sentError: ErrorTransmissionMessage[];
  restaurantStaffIntervention: RestaurantStaffInterventionStatus;
  sendHITLSessionStart: IHITLSessionStartTransmissionMessage[];
  sendHITLSessionEnd: IHITLSessionEndTransmissionMessage[];
  sendHITLEventConnection: IHITLEventConnectionTransmissionMessage[];
  sbFinalTextFrames: TextFrameTransmissionMessage[]; //Received via session buffer API regarding conversations happened before restaurant specific HITL page
  sbInProgressHypothesisTextFrames: TextFrameTransmissionMessage[]; //Received via session buffer API regarding conversations happened before restaurant specific HITL page
  sendCancelOrder: ICancelOrderTransmissionMessage[];
  entityAnnotationsMap: Record<string, EntitySpan[]>;
}

export interface BufferedAudioState {
  frames: AudioFrameTransmissionMessage[];
  indexLocations: { [seq: number]: number };
  bufferedSeqs: number[];
  lastBufferFlush: number;
}

export const initialState: MessagingState = {
  isEstablishingConnection: false,
  isConnected: false,
  isAudioWSConnected: false,
  isEstablishingAudioWSConnection: false,
  eventWSConnectionMessage: '',
  audioWSConnectionMessage: '',
  eventWSConnectionSuccess: false,
  audioWSConnectionSuccess: false,
  eventWSConnectionDurationMs: null,
  audioWSConnectionDurationMs: null,
  currentSessionId: `${uuidv4()}`,
  sentTextFrames: [],
  sentInfo: [],
  sentEnd: [],
  sendOrder: [],
  sendAgentCart: [],
  sendLoyalty: [],
  sendAgentFirstActivity: [],
  sendAgentFirstInterception: [],
  sendAgentInterception: [],
  sendAgentMetric: [],
  sendAgentSuggestionResponse: [],
  sendStaffInterception: [],
  inProgressHypothesisTextFrames: [],
  finalTextFrames: [],
  startFrame: null,
  hypothesisFrame: null,
  handledHypothesisFrames: {},
  isRestaurantStaffJoined: {},
  isAIActive: false, // It's true when the restaurant has AI on. It is false when restaurant staff intervention happens.
  isPlaying: false,
  isNewSession: false,
  isCarPresent: false,
  isStaffIntervention: false,
  isStaffIntervenedBeforeCarExit: false,
  sessionSource: SessionSource.hitl,
  isTTSOn: false,
  sentTTSRequest: [],
  sentError: [],
  restaurantStaffIntervention: RestaurantStaffInterventionStatus.initial,
  sendHITLSessionStart: [],
  sendHITLSessionEnd: [],
  sendHITLEventConnection: [],
  sbFinalTextFrames: [],
  sbInProgressHypothesisTextFrames: [],
  sendCancelOrder: [],
  entityAnnotationsMap: {},
};

interface IBufferMessagesResponseType {
  events: TransmissionMessage[];
}

export const getSessionBufferMessages = createAsyncThunk(
  'session/bufferMessages',
  async (
    {
      isCurrentTaskUnsupervised = false,
      timeout = 60000,
      checkAIMessages = false,
    }: {
      isCurrentTaskUnsupervised?: boolean;
      timeout?: number;
      checkAIMessages?: boolean;
    },
    { dispatch, getState, rejectWithValue }
  ) => {
    const {
      taskRouter,
      config: { SESSION_BUFFER_API, SESSION_BUFFER_API_AUTHENTICATION },
      ai,
    } = getState();

    let agent_types = '';
    if (checkAIMessages) {
      agent_types = AgentTypes.llm;
    } else {
      agent_types =
        AgentTypes.orderBoard +
        `${
          isCurrentTaskUnsupervised
            ? ',' + AgentTypes.llm + ',' + AgentTypes.menuModule
            : ''
        }`;
    }

    Promise.race([
      get({
        url: `${SESSION_BUFFER_API}/sessions/${taskRouter.taskAssignedSessionId}/events?agent_type=${agent_types}`,
        headers: {
          Authorization: `Bearer ${SESSION_BUFFER_API_AUTHENTICATION}`,
        },
        successCallback: (response: { data: IBufferMessagesResponseType }) => {
          const receivedEvents = response?.data?.events || [];
          const bufferedMessages = isCurrentTaskUnsupervised
            ? filterBufferedEvents(receivedEvents)
            : receivedEvents;

          if (bufferedMessages.length) {
            logger.debug({
              message:
                'Received & filtered out the following buffered messages',
              count: bufferedMessages.length,
            });
            dispatch(
              messagingActions.messageReceived({
                messages: bufferedMessages,
                isSessionBufferedMessages: true,
                taskRouter,
                ai,
              })
            );
          }
          dispatch(
            updateNonCriticalCheckScore({
              name: NonCriticalChecksNames.apiSessionBuffer,
              value: 1,
            })
          );
        },
        errorCallback: (error) => {
          dispatch(
            updateNonCriticalCheckScore({
              name: NonCriticalChecksNames.apiSessionBuffer,
              value: 0,
            })
          );
          logger.warn({
            message: 'Failed to get buffered messages',
            error,
          });
          return rejectWithValue(error);
        },
      }),
      new Promise((resolve) => {
        dispatch(
          updateNonCriticalCheckScore({
            name: NonCriticalChecksNames.apiSessionBuffer,
            value: 0,
          })
        );
        setTimeout(
          resolve,
          timeout,
          new Error(
            'Timeout to get buffered events messages from Session Buffer API'
          )
        );
      }),
    ]).then((res) => {
      if (res instanceof Error) {
        logger.warn(res);
      }
    });
  }
);

type BaseNetworkCallLogsData = {
  url: string;
  message: string;
  /** time in milliseconds */
  duration: number;
};

type NetworkCallLogsData = BaseNetworkCallLogsData &
  (
    | {
        /** network calls made via which api */
        via: 'websocket' | 'fetch' | 'axios' | 'axios_api_wrapper';
      }
    | {
        /** graphql operation */
        graphQLOperation: string;
        /** network calls made via which api */
        via: 'graphql_sdk';
      }
  );

export const sendNetworkCallLogs = createAsyncThunk(
  'messaging/sendNetworkCallLogs',
  async (
    { message, ...metadata }: NetworkCallLogsData,
    { getState, dispatch }
  ) => {
    const { config, sessionBoundary, messages, restaurant, taskRouter } =
      getState();
    const isWebSocketConnected = selectIsWebSocketConnected(getState());

    const infoData: Pick<InfoTransmissionMessage, 'data'> = {
      data: { type: 'METRIC', message, metadata },
    };

    if (isWebSocketConnected) {
      dispatch(messagingActions.sendInfo(infoData));
      return;
    }

    // send event via event backend's REST API
    const infoEvent: InfoTransmissionMessage = {
      id: uuidv4(),
      seq: infoSeqIdCounter.increment(),
      event: EventTypes.info,
      session_id:
        taskRouter?.taskAssignedSessionId ||
        sessionBoundary?.sessionId ||
        messages?.currentSessionId,
      agent_type: AgentTypes.HITL,
      agent_id: agent.getAgentId(),
      timestamp: new Date().toISOString(),
      metadata: {
        environment: readEnvVariable('DEPLOY_ENV') || 'unknown',
      },
      restaurant_code:
        restaurant.selectedRestaurantDetails?.restaurantCode ||
        DUMMY_RESTAURANT_CODE,
      ...infoData,
    };

    const url = `${config.EVENT_API}/v1/event`;

    await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Basic ${config.EVENT_API_BASIC_AUTH_PARAM}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(infoEvent),
    }).catch((err) => {
      logger.warn(err);
    });
  }
);

export const sendTestSignal = createAsyncThunk(
  'messaging/sendTestSignal',
  async (undefined, { getState, dispatch }) => {
    const {
      config,
      sessionBoundary: { sessionId },
      messages: { currentSessionId },
    } = getState();

    // send event via event backend's REST API
    const infoEvent: InfoTransmissionMessage = {
      id: uuidv4(),
      seq: infoSeqIdCounter.increment(),
      event: EventTypes.info,
      session_id: sessionId || currentSessionId || 'dummy_session_id',
      agent_type: AgentTypes.HITL,
      agent_id: agent.getAgentId(),
      timestamp: new Date().toISOString(),
      metadata: {
        environment: readEnvVariable('DEPLOY_ENV') || 'unknown',
      },
      data: {
        type: 'METRIC',
        message: 'test rest api signal from hitl',
      },
    };

    const url = `${config.EVENT_API}/v1/event`;

    await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Basic ${config.EVENT_API_BASIC_AUTH_PARAM}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(infoEvent),
    }).catch((err) => {
      logger.warn(err);
    });
  }
);

export const messagingSlice = createSlice({
  name: 'messaging',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    startConnecting: (state) => {
      state.isConnected = false;
      state.isEstablishingConnection = true;
    },
    connectionEstablished: (state) => {
      state.isConnected = true;
      state.isEstablishingConnection = false;
      state.eventWSConnectionMessage = '';
      state.eventWSConnectionSuccess = false;
    },
    connectionLost: (state) => {
      state.isConnected = false;
      state.isEstablishingConnection = true;
    },
    closeConnection: (state) => {
      state.isConnected = false;
      state.isEstablishingConnection = false;
      state.eventWSConnectionMessage = '';
    },
    setEventWSConnectionMessage: (state, action: PayloadAction<string>) => {
      state.eventWSConnectionMessage = action.payload;
      state.eventWSConnectionSuccess = false;
    },
    setAudioWSConnectionMessage: (state, action: PayloadAction<string>) => {
      state.audioWSConnectionMessage = action.payload;
      state.audioWSConnectionSuccess = false;
    },
    setEventWSConnectionSuccess: (state, action: PayloadAction<boolean>) => {
      state.eventWSConnectionSuccess = action.payload;
    },
    setAudioWSConnectionSuccess: (state, action: PayloadAction<boolean>) => {
      state.audioWSConnectionSuccess = action.payload;
    },
    startAudioWSConnecting: (state) => {
      state.isAudioWSConnected = false;
      state.isEstablishingAudioWSConnection = true;
    },
    audioWSConnectionEstablished: (state) => {
      state.isAudioWSConnected = true;
      state.isEstablishingAudioWSConnection = false;
      state.audioWSConnectionMessage = '';
      state.audioWSConnectionSuccess = false;
    },
    setEventWSConnectionDuration: (state, action: PayloadAction<number>) => {
      state.eventWSConnectionDurationMs = action.payload;
    },
    setAudioWSConnectionDuration: (state, action: PayloadAction<number>) => {
      state.audioWSConnectionDurationMs = action.payload;
    },
    audioWSConnectionLost: (state) => {
      state.isAudioWSConnected = false;
      state.isEstablishingAudioWSConnection = true;
    },
    closeAudioWSConnection: (state) => {
      state.isAudioWSConnected = false;
      state.isEstablishingAudioWSConnection = false;
      state.audioWSConnectionMessage = '';
    },
    messageReceived: (state, action: PayloadAction<IMessageReceived>) => {
      const {
        messages = [],
        taskRouter,
        isSessionBufferedMessages,
      } = action.payload;
      messages.forEach((message) => {
        if (
          [
            EventTypes.check,
            EventTypes.orderStatus,
            EventTypes.loyaltyStatus,
            EventTypes.connect,
            EventTypes.disconnect,
            EventTypes.aiEscalation,
            EventTypes.aiSessionState,
            EventTypes.order,
            EventTypes.aiSuggestion,
          ].includes(message.event)
        ) {
          //These messages are handled in other slices
          return;
        }

        if (
          taskRouter?.currentTaskId &&
          taskRouter?.taskAssignedSessionId !== message.session_id
        ) {
          //If the current task is from task-router and if the message session is not matching with
          //task-router current task session, don't process the received message - exception is TTS ON/OFF messages
          if (![EventTypes.TTSOff, EventTypes.TTSOn].includes(message.event))
            return;
        }

        if (
          message.session_id !== OUT_OF_SESSION &&
          state.currentSessionId !== message.session_id
        ) {
          state.currentSessionId = message.session_id;
          state.sessionSource = SessionSource.communicator;
        }
        switch (message.event) {
          case EventTypes.start:
            // TODO handle new start frame
            state.startFrame = message as NewTransmissionMessage;
            break;
          // WS-1 (HITL-1411): audio no longer flows through Redux. The socket
          // middleware plays guest (ORDERBOARD) audio directly via the
          // `audioPlayer` singleton and never dispatches audio frames, so there
          // is no `EventTypes.audio` case here anymore.
          case EventTypes.text:
            const textEvent = message as TextFrameTransmissionMessage;
            // TODO identify why we're getting an echo
            if (state.sentTextFrames.map((f) => f.id).includes(textEvent.id)) {
              break;
            }
            if (isSessionBufferedMessages) {
              if (textEvent.data.status === 'hypothesis') {
                state.sbInProgressHypothesisTextFrames.push(textEvent);
              } else if (textEvent.data.status === 'TTS') {
                state.sbFinalTextFrames.push(textEvent);
                const textPayload = textEvent.data.payload;
                if (!textEvent.data.hitl_mute && textPayload) {
                  cyborgAlert(textPayload);
                }
              } else {
                state.sbFinalTextFrames.push(textEvent);
                // Clear out all "in progress" text frames
                state.sbInProgressHypothesisTextFrames.length = 0;
              }
              break;
            }

            // TODO check seq id is larger than last
            if (textEvent.data.status === 'hypothesis') {
              state.inProgressHypothesisTextFrames.push(textEvent);
            } else if (textEvent.data.status === 'TTS') {
              state.finalTextFrames.push(textEvent);
              const textPayload = textEvent.data.payload;
              if (!textEvent.data.hitl_mute && textPayload) {
                cyborgAlert(textPayload);
              }
            } else {
              state.finalTextFrames.push(textEvent);
              // Clear out all "in progress" text frames
              state.inProgressHypothesisTextFrames.length = 0;
            }
            break;
          case EventTypes.hypothesis:
            const hypothesisFrame = message as HypothesisTransmissionMessage;
            if (state.currentSessionId === hypothesisFrame.session_id) {
              state.hypothesisFrame = message as HypothesisTransmissionMessage;
            }
            break;
          case EventTypes.startSession:
            state.isNewSession = true;
            state.isStaffIntervention = false;
            state.restaurantStaffIntervention =
              RestaurantStaffInterventionStatus.initial;
            state.isStaffIntervenedBeforeCarExit = false;
            break;
          case EventTypes.carEnter: {
            state.isNewSession = true;
            state.isCarPresent = true;
            state.restaurantStaffIntervention =
              RestaurantStaffInterventionStatus.initial;
            if (!isSessionBufferedMessages) {
              state.inProgressHypothesisTextFrames = [];
              state.finalTextFrames = [];
              state.entityAnnotationsMap = {};
            }
            state.isStaffIntervenedBeforeCarExit = false;
            break;
          }
          case EventTypes.carExit:
            state.isNewSession = false;
            state.isCarPresent = false;
            state.isStaffIntervenedBeforeCarExit = state.isStaffIntervention;
            state.isStaffIntervention = false;
            // The departed car's hypothesis must not outlive it: the HITL 3.0
            // car-exit reset flips isAIAutoMode back on (CustomerPulse), and a
            // lingering frame would then be re-applied by useHypothesisFrames —
            // clearCart() + rebuild — reverting any agent edits made during an
            // intercept. The session-boundary sessionId and the (still false)
            // finalOrderSubmitted don't guard this window, so the frame itself
            // has to go.
            state.hypothesisFrame = null;
            break;
          case EventTypes.info:
            if (
              (message as StaffInfoTransmissionMessage).data.payload ===
              'StaffInterVention'
            ) {
              state.isStaffIntervention = true;
              state.isAIActive = false;
              state.hypothesisFrame = null;
              break;
            }
            break;
          case EventTypes.TTSOff:
            state.isTTSOn = false;
            break;
          case EventTypes.TTSOn:
            state.isTTSOn = true;
            break;
          case EventTypes.aiStatus:
            state.isAIActive = true;
            break;
          case EventTypes.transcriptHighlight: {
            const annotationMsg =
              message as IEntityAnnotationTransmissionMessage;
            const { source_text_event_id, entities } = annotationMsg.data ?? {};
            if (
              source_text_event_id &&
              entities &&
              !state.entityAnnotationsMap[source_text_event_id]
            ) {
              state.entityAnnotationsMap[source_text_event_id] = entities;
            }
            break;
          }
          case EventTypes.staffIntervention:
            state.isStaffIntervention = true;
            state.isAIActive = false;
            state.hypothesisFrame = null;
            break;
          default:
            // Suppress error for dynamic property access or check property existence before accessing 'type'
            // @ts-ignore
            if (message['type'] !== 'ping') {
              logger.debug({
                message: `Received an unhandled message event with type ${message.event}`,
              });
            }
        }
      });
    },
    sendMessage: (
      state,
      action: PayloadAction<TextFrameTransmissionMessage>
    ) => {
      state.sentTextFrames.push(action.payload);
    },
    sendInfo: (
      state,
      action: PayloadAction<Pick<InfoTransmissionMessage, 'data'>>
    ) => {
      state.sentInfo.push(action.payload);
    },
    sendAgentFirstActivity: (
      state,
      action: PayloadAction<IHITLAgentFirstActivityTransmissionMessage>
    ) => {
      state.sendAgentFirstActivity.push(action.payload);
    },
    sendAgentFirstInterception: (
      state,
      action: PayloadAction<Pick<AgentInterceptionMessage, 'data'>>
    ) => {
      state.sendAgentFirstInterception.push(action.payload);
    },
    sendAgentInterception: (
      state,
      action: PayloadAction<Pick<AgentInterceptionMessage, 'data'>>
    ) => {
      state.sendAgentInterception.push(action.payload);
    },
    sendAgentMetric: (
      state,
      action: PayloadAction<Pick<IAgentMetricTransmissionMessage, 'data'>>
    ) => {
      state.sendAgentMetric.push(
        action.payload as IAgentMetricTransmissionMessage
      );
    },
    sendAgentSuggestionResponse: (
      state,
      action: PayloadAction<
        Pick<IAgentSuggestionResponseTransmissionMessage, 'data'>
      >
    ) => {
      state.sendAgentSuggestionResponse.push(
        action.payload as IAgentSuggestionResponseTransmissionMessage
      );
    },
    sendStaffInterception: (
      state,
      action: PayloadAction<Pick<StaffInterceptionMessage, 'data'>>
    ) => {
      state.sendStaffInterception.push(action.payload);
    },
    handleHypothesisFrame: (
      state,
      action: PayloadAction<{ sessionId: string; index: number }>
    ) => {
      // the order item will be pushed to hypothesis array in sequence so keep the idex of order item already handled to reduce duplicate rendering
      state.handledHypothesisFrames[action.payload.sessionId].push(
        action.payload.index
      );
    },
    sendEndSession: (
      state,
      action: PayloadAction<EndSessionTransmissionMessage>
    ) => {
      state.sentEnd.push(action.payload);
    },
    sendOrder: (state, action: PayloadAction<IOrderTransmissionMessage>) => {
      state.sendOrder.push(action.payload);
    },
    sendAgentCart: (
      state,
      action: PayloadAction<Pick<IAgentCartTransmissionMessage, 'data'>>
    ) => {
      state.sendAgentCart.push(action.payload as IAgentCartTransmissionMessage);
    },
    sendLoyalty: (
      state,
      action: PayloadAction<ILoyaltyTransmissionMessage>
    ) => {
      state.sendLoyalty.push(action.payload);
    },
    closeRestaurantStaffJoined: (
      state,
      action: PayloadAction<{ sessionId: string | null }>
    ) => {
      if (state.isRestaurantStaffJoined && action.payload.sessionId) {
        state.isRestaurantStaffJoined[action.payload.sessionId] = false;
      }
    },
    clearMessages: (state) => {
      state.finalTextFrames = [];
      state.inProgressHypothesisTextFrames = [];
      state.sbFinalTextFrames = [];
      state.sbInProgressHypothesisTextFrames = [];
      state.sentTextFrames = [];
      state.sendAgentFirstActivity = [];
      state.sendAgentFirstInterception = [];
      state.sendAgentInterception = [];
      state.sendStaffInterception = [];
      state.entityAnnotationsMap = {};
    },
    setIsPlaying: (state, action: PayloadAction<boolean>) => {
      // WS-1 (HITL-1411): pure reducer. The audioPlayer start/stop lifecycle is
      // driven by the socket middleware intercepting this action, so the reducer
      // no longer touches a non-serializable player instance.
      state.isPlaying = action.payload;
    },
    setIsAIActive: (state, action: PayloadAction<boolean>) => {
      state.isAIActive = action.payload; // isAIActive is reset by getAIControlPanelStatus when restaurant is selected and when hitl session ends
    },
    setRestaurantStaffIntervention: (
      state,
      action: PayloadAction<RestaurantStaffInterventionStatus>
    ) => {
      state.restaurantStaffIntervention = action.payload;
    },
    resetCarState: (state) => {
      state.isNewSession = false;
      state.isCarPresent = false;
      state.restaurantStaffIntervention =
        RestaurantStaffInterventionStatus.initial;
      state.isStaffIntervention = false;
    },
    setStaffIntervention: (state, action: PayloadAction<boolean>) => {
      state.isStaffIntervention = action.payload;
    },
    sendTTSRequest: (state, action: PayloadAction<TransmissionMessage>) => {
      state.sentTTSRequest.push(action.payload);
    },
    sendError: (state, action: PayloadAction<ErrorTransmissionMessage>) => {
      state.sentError.push(action.payload);
    },
    resetHypothesisFrame: (state) => {
      state.hypothesisFrame = null;
    },
    carEnter: (state) => {
      state.isNewSession = true;
      state.isCarPresent = true;
      state.restaurantStaffIntervention =
        RestaurantStaffInterventionStatus.initial;
    },
    sendHITLSessionStart: (
      state,
      action: PayloadAction<IHITLSessionStartTransmissionMessage>
    ) => {
      state.sendHITLSessionStart.push(action.payload);
    },
    sendHITLSessionEnd: (
      state,
      action: PayloadAction<IHITLSessionEndTransmissionMessage>
    ) => {
      state.sendHITLSessionEnd.push(action.payload);
    },
    sendHITLEventConnection: (
      state,
      action: PayloadAction<IHITLEventConnectionTransmissionMessage>
    ) => {
      state.sendHITLEventConnection.push(action.payload);
    },
    sendCancelOrder: (
      state,
      action: PayloadAction<ICancelOrderTransmissionMessage>
    ) => {
      state.sendCancelOrder.push(action.payload);
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(selectStage.fulfilled, selectMenuVersion.fulfilled),
      (state, action) => {
        state.hypothesisFrame = null;
      }
    );
  },
});

export const messagingActions = messagingSlice.actions;
export const {
  setIsAIActive,
  setStaffIntervention,
  resetHypothesisFrame,
  sendOrder,
  sendLoyalty,
  sendHITLSessionStart,
  sendHITLSessionEnd,
  startAudioWSConnecting,
  audioWSConnectionEstablished,
  setEventWSConnectionDuration,
  setAudioWSConnectionDuration,
  audioWSConnectionLost,
  closeAudioWSConnection,
  sendCancelOrder,
  clearMessages,
} = messagingSlice.actions;

export default messagingSlice.reducer;
